import { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { ChatIcon, CircledCheckmarkIcon, ClockIcon, EmailIcon, PhoneIcon, RedXIcon, WarningIcon } from '../../atoms/Icons';
import FeedFloToolTip from '../../atoms/FeedFloToolTip';
import { censorTypes, useCensor } from '../../utils/hooks/useCensor';
import useUser from '../../utils/hooks/useUser';
import withStaffOnlyIdWrapper from '../../utils/withStaffOnlyIdWrapper';
import { MESSAGE_HISTORY_MODE_DB_TO_UI } from '../../utils/messageHistoryUrl';

const MessageIdCopyButton = withStaffOnlyIdWrapper('message id');

const TRANSPORT_MODE_CONFIG = {
  push: { Icon: PhoneIcon, label: 'App' },
  email: { Icon: EmailIcon, label: 'Email' },
  sms: { Icon: ChatIcon, label: 'SMS' },
};

const DELIVERY_STATUS_CONFIG = {
  delivered: { Icon: CircledCheckmarkIcon, label: 'Delivered' },
  failed: { Icon: RedXIcon, label: 'Failed' },
  pending: { Icon: ClockIcon, label: 'Pending' },
  unsubscribed: { Icon: WarningIcon, label: 'User unsubscribed' },
};

function MessageHistoryTableRow({
  isHeader = false,
  messageId = null,
  date = '',
  time = '',
  userId = null,
  userName = '',
  organizationId = null,
  transportedAs = '',
  status = null,
  entries = [],
}) {
  const { censor } = useCensor();
  const { user } = useUser();
  const detailsRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const detailsElement = detailsRef.current;
    if (!detailsElement || isExpanded) {
      return undefined;
    }

    const measureOverflow = () => {
      setIsOverflowing(detailsElement.scrollHeight > detailsElement.clientHeight + 1);
    };

    measureOverflow();

    const resizeObserver = new ResizeObserver(measureOverflow);
    resizeObserver.observe(detailsElement);

    return () => resizeObserver.disconnect();
  }, [entries, isExpanded]);

  const showExpandControl = isExpanded || isOverflowing;

  const renderUser = () => {
    if (userId) {
      return (
        <Link
          className="MessageHistoryTable-userLink"
          to={organizationId ? `/orgs/${organizationId}/users/${userId}` : `/users/${userId}`}
        >
          {userName}
        </Link>
      );
    }

    return userName;
  };

  const renderBarn = (entry) => {
    const barnName = censor(entry.barn, censorTypes.barn);

    if (entry.barnId && entry.barn !== '—') {
      return (
        <Link className="MessageHistoryTable-barnLink" to={`/b/${entry.barnId}`}>
          {barnName}
        </Link>
      );
    }

    return barnName;
  };

  if (isHeader) {
    return (
      <div className="MessageHistoryTable-row MessageHistoryTable-row--header">
        <div className="MessageHistoryTable-headerCell MessageHistoryTable-headerCell--mode" aria-hidden="true" />
        <div className="MessageHistoryTable-headerCell MessageHistoryTable-headerCell--status" aria-hidden="true" />
        <div className="MessageHistoryTable-headerCell">Date</div>
        <div className="MessageHistoryTable-headerCell">User</div>
        <div className="MessageHistoryTable-headerCell">Barn - Feedline - Type - Reason</div>
      </div>
    );
  }

  const transportConfig = TRANSPORT_MODE_CONFIG[transportedAs];
  const TransportIcon = transportConfig?.Icon;
  const transportLabel = transportConfig?.label || MESSAGE_HISTORY_MODE_DB_TO_UI[transportedAs] || transportedAs;

  const deliveryStatusConfig = status?.status ? DELIVERY_STATUS_CONFIG[status.status] : null;
  const DeliveryStatusIcon = deliveryStatusConfig?.Icon || null;

  const buildStatusTooltip = () => {
    if (!status?.status) return null;
    const lines = [deliveryStatusConfig?.label || status.label];
    if (status.timestamp) lines.push(status.timestamp);
    if (status.detail) lines.push(status.detail);
    return lines.join('\n');
  };

  const statusTooltipText = buildStatusTooltip();
  const statusAriaLabel = statusTooltipText?.split('\n')[0] || 'Unknown status';

  return (
    <div className="MessageHistoryTable-row">
      <div className="MessageHistoryTable-mode">
        {TransportIcon ? (
          <FeedFloToolTip description={transportLabel} position="top">
            <span className="MessageHistoryTable-modeIcon" aria-label={transportLabel}>
              <TransportIcon className="MessageHistoryTable-transportIcon" />
            </span>
          </FeedFloToolTip>
        ) : (
          <span className="MessageHistoryTable-rowText">—</span>
        )}
      </div>
      <div className="MessageHistoryTable-status">
        {DeliveryStatusIcon ? (
          <FeedFloToolTip description={statusTooltipText} position="top" multiline>
            <span
              className={`MessageHistoryTable-statusIcon MessageHistoryTable-statusIcon--${status.variant}`}
              aria-label={statusAriaLabel}
            >
              <DeliveryStatusIcon className="MessageHistoryTable-statusIconSvg" />
            </span>
          </FeedFloToolTip>
        ) : (
          <span className="MessageHistoryTable-rowText">—</span>
        )}
      </div>
      <div className="MessageHistoryTable-rowText">
        <div className="MessageHistoryTable-dateLine">
          <span>{date}</span>
        </div>
        <div className="MessageHistoryTable-rowText--subtext">{time}</div>
        {messageId && (
          <MessageIdCopyButton data={messageId} hoverText="Copy message ID" className="MessageHistoryTable-copyButton" />
        )}
      </div>
      <div
        className={`MessageHistoryTable-rowText MessageHistoryTable-user${
          !isExpanded ? ' MessageHistoryTable-clampedText' : ''
        }`}
      >
        {renderUser()}
      </div>
      <div className="MessageHistoryTable-detailsCell">
        <div
          ref={detailsRef}
          className={`MessageHistoryTable-details${isExpanded ? ' MessageHistoryTable-details--expanded' : ''}`}
        >
          {entries.map((entry, index) => (
            <div
              key={entry.faultId || `${entry.barnId || entry.barn}-${entry.feedline}-${entry.type}-${index}`}
              className="MessageHistoryTable-detailLine"
            >
              <span>{renderBarn(entry)}</span>
              <span className="MessageHistoryTable-detailSeparator"> - </span>
              <span>{entry.feedline}</span>
              <span className="MessageHistoryTable-detailSeparator"> - </span>
              <span>{entry.type}</span>
              <span className="MessageHistoryTable-detailSeparator"> - </span>
              <span>{entry.reason}</span>
              {user?.isStaff && entry.faultId && (
                <>
                  {' '}
                  <Link
                    className="MessageHistoryTable-faultLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    to={`/fault/${entry.faultId}`}
                    title="View fault"
                  >
                    🔎
                  </Link>
                </>
              )}
            </div>
          ))}
        </div>
        {showExpandControl ? (
          <button
            type="button"
            className="MessageHistoryTable-expandButton"
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

MessageHistoryTableRow.propTypes = {
  isHeader: PropTypes.bool,
  messageId: PropTypes.string,
  date: PropTypes.string,
  time: PropTypes.string,
  userId: PropTypes.string,
  userName: PropTypes.string,
  organizationId: PropTypes.string,
  transportedAs: PropTypes.string,
  status: PropTypes.shape({
    status: PropTypes.string,
    label: PropTypes.string,
    variant: PropTypes.string,
    timestamp: PropTypes.string,
    detail: PropTypes.string,
  }),
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      barn: PropTypes.string,
      barnId: PropTypes.string,
      feedline: PropTypes.string,
      type: PropTypes.string,
      reason: PropTypes.string,
      faultId: PropTypes.string,
    }),
  ),
};

export default MessageHistoryTableRow;
